import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { CommunityFacade } from '../../features/communities/community.facade';
import { TrainingRepositoryService } from '../trainings/training-repository.service';
import { can, hasRole } from '../../features/communities/domain/community-permissions';
import type { UserCommunityEntry } from '../../features/communities/domain/community.models';

const guardFor = (check: (membership: UserCommunityEntry | null) => boolean): CanActivateFn =>
  async (route) => {
    // Resolve all dependencies synchronously while the injection context is active (before any await).
    const router = inject(Router);
    const auth = inject(AuthService);
    const facade = inject(CommunityFacade);

    const cid = route.paramMap.get('id');
    if (!cid) {
      return router.createUrlTree(['/app/communities']);
    }
    await auth.whenReady();
    const entries = await facade.ensureMyCommunities();
    const membership = entries.find((entry) => entry.cid === cid && entry.status === 'active') ?? null;
    return check(membership) ? true : router.createUrlTree(['/app/communities', cid]);
  };

/** Any active member of the community may pass. */
export const communityMemberGuard: CanActivateFn = guardFor((m) => hasRole(m, 'member'));

/** admin/owner only — member & group management. */
export const communityManagerGuard: CanActivateFn = guardFor((m) => can(m, 'manageMembers'));

/** Dedicated capability for the administration pages of optional community tools. */
export const communityModuleAdminGuard: CanActivateFn = guardFor((m) => can(m, 'manageModules'));

/** trainer/admin/owner — training scheduling & game building. */
export const trainerGuard: CanActivateFn = guardFor((m) => hasRole(m, 'trainer'));

/** trainer/admin/owner for a route whose :id param is a training id. */
export const trainingTrainerGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const communities = inject(CommunityFacade);
  const trainings = inject(TrainingRepositoryService);
  const tid = route.paramMap.get('id');
  if (!tid) {
    return router.createUrlTree(['/app/trainings']);
  }
  await auth.whenReady();
  const training = await trainings.loadTraining(tid);
  if (!training) {
    return router.createUrlTree(['/app/trainings']);
  }
  const entries = await communities.ensureMyCommunities();
  const membership = entries.find((entry) => entry.cid === training.cid && entry.status === 'active') ?? null;
  return hasRole(membership, 'trainer') ? true : router.createUrlTree(['/app/trainings', tid]);
};
